import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LockKey, SignOut, Storefront, CalendarCheck, ShoppingBag } from "@phosphor-icons/react";
import Logo from "../components/Logo";
import { api } from "../lib/api";

const STORAGE_KEY = "ml-admin-key";

function ServiceBadge({ label }) {
  const isBookings = label === "Table bookings";
  const Icon = isBookings ? CalendarCheck : ShoppingBag;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#FF1F8B]/10 text-[#FF1F8B]">
      <Icon size={11} weight="fill" />
      {label}
    </span>
  );
}

export default function AdminSignups() {
  const [key, setKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [input, setInput] = useState(key);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k = key) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/partners/signups", { headers: { "X-Admin-Key": k } });
      setRows(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setKey("");
        setInput("");
        setRows([]);
        setError("That key didn't work. Check it and try again.");
      } else {
        setError("Couldn't load applications right now. Try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    localStorage.setItem(STORAGE_KEY, value);
    setKey(value);
    load(value);
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setKey("");
    setInput("");
    setRows([]);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 pt-8 md:pt-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="admin-back-home"
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft size={16} weight="bold" />
          Back to My Local
        </Link>
        <Logo size="md" withText={false} />
      </div>

      {!key ? (
        <div className="max-w-md mx-auto px-6 pt-16 md:pt-24">
          <div className="rounded-[28px] bg-white border border-zinc-100 shadow-[0_30px_80px_-30px_rgba(15,15,20,0.25)] p-8 md:p-10">
            <div className="absolute" />
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl brand-gradient text-white mb-6">
              <LockKey size={22} weight="duotone" />
            </span>
            <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-zinc-900">
              Partner applications
            </h1>
            <p className="mt-2 text-sm text-zinc-500">
              Private area. Enter your admin key to see who's applied.
            </p>
            <form onSubmit={save} className="mt-6 space-y-4" data-testid="admin-login-form">
              <input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Admin key"
                autoComplete="off"
                data-testid="admin-key-input"
                className="w-full rounded-2xl bg-white border border-zinc-200 px-4 py-3.5 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-[#FF1F8B] focus:ring-4 focus:ring-[#FF1F8B]/10 transition-all"
              />
              <button
                type="submit"
                data-testid="admin-login-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full brand-gradient text-white font-semibold hover:-translate-y-0.5 transition-transform"
              >
                View applications
              </button>
              {error && (
                <p className="text-sm text-red-600" data-testid="admin-error">{error}</p>
              )}
            </form>
          </div>
        </div>
      ) : (
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-20">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="overline text-zinc-500 mb-2 flex items-center gap-2">
                <Storefront size={14} weight="duotone" className="text-[#FF1F8B]" />
                Private
              </p>
              <h1 className="font-display font-extrabold text-3xl md:text-5xl tracking-tighter text-zinc-900">
                Partner <span className="gradient-text">applications.</span>
              </h1>
              <p className="mt-2 text-sm text-zinc-500" data-testid="admin-count">
                {loading ? "Loading…" : `${rows.length} application${rows.length === 1 ? "" : "s"}`}
              </p>
            </div>
            <button
              onClick={signOut}
              data-testid="admin-signout-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-50 transition-colors"
            >
              <SignOut size={15} weight="bold" />
              Forget key
            </button>
          </div>

          {error && <p className="text-sm text-red-600 mb-4" data-testid="admin-error">{error}</p>}

          {!loading && !error && rows.length === 0 && (
            <div className="rounded-3xl bg-white border border-zinc-100 p-10 text-center" data-testid="admin-empty">
              <p className="font-display font-bold text-xl text-zinc-900">No applications yet.</p>
              <p className="mt-2 text-sm text-zinc-500">New ones land here, and in your inbox, the moment someone applies.</p>
            </div>
          )}

          <div className="space-y-4" data-testid="admin-signups-list">
            {rows.map((r) => (
              <div
                key={r.id}
                data-testid={`signup-row-${r.id}`}
                className="rounded-3xl bg-white border border-zinc-100 p-6 md:p-7"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display font-extrabold text-xl tracking-tight text-zinc-900">
                      {r.business_name}
                    </h2>
                    <p className="text-sm text-zinc-500 mt-0.5">
                      {r.contact_name} · {r.business_type}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(r.services || []).map((s) => (
                      <ServiceBadge key={s} label={s} />
                    ))}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                  <p className="text-zinc-600">
                    <span className="text-zinc-400">Email:</span>{" "}
                    <a href={`mailto:${r.email}`} className="text-[#FF1F8B] hover:underline">{r.email}</a>
                  </p>
                  <p className="text-zinc-600"><span className="text-zinc-400">Phone:</span> {r.phone}</p>
                  <p className="text-zinc-600 sm:col-span-2"><span className="text-zinc-400">Address:</span> {r.address}</p>
                  {r.message && (
                    <p className="text-zinc-600 sm:col-span-2"><span className="text-zinc-400">Message:</span> {r.message}</p>
                  )}
                </div>
                <p className="mt-4 text-xs text-zinc-400">
                  Applied {r.created_at ? new Date(r.created_at).toLocaleString("en-GB") : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
